import React from 'react';
import clsx from 'clsx';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { MinusIcon, PlusIcon } from '@heroicons/react/24/outline';

import { Button } from '@oysterjs/ui/button';
import { Checkbox, CheckboxField } from '@oysterjs/ui/checkbox';
import {
  Dialog,
  DialogActions,
  DialogBody,
  DialogDescription,
  DialogTitle
} from '@oysterjs/ui/dialog';
import {
  Description,
  ErrorMessage,
  Field,
  FieldGroup,
  Fieldset,
  Label
} from '@oysterjs/ui/fieldset';
import { Input } from '@oysterjs/ui/input';
import { Spinner } from '@oysterjs/ui/spinner';
import {
  LoggedInAgencyUserFieldsFragment,
  SubscriptionPeriod,
  SubscriptionPlan
} from '~/api/graphql';

import {
  finalizeSubscriptionCheckout,
  openBillingPortal,
  previewSubscriptionCheckout,
  SubscriptionCheckoutPreview
} from './complete-subscription-checkout';
import { InvoicePreview, InvoicePreviewSkeleton } from './invoice-preview';
import { RENEWAL_PLAN_OPTIONS, RENEWAL_CALL_URL, STANDARD_PLAN_TERMS_URL } from './annual-renewal';
import { SUPPORT_EMAIL } from './renewal-migration-shared';
import { RenewalMigrationPaymentForm } from './renewal-migration-payment-form';
import { RenewalPlanDetailsActions, RenewalPlanDetailsBody } from './renewal-plan-details-dialog';

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
const stripePublicKey = process.env.STRIPE_PUBLIC_KEY || window.oyster?.opts?.stripePublicKey || '';
const stripePromise = loadStripe(stripePublicKey);

export const RenewalMigrationWizard = (props: {
  open: boolean;
  onClose: () => void;
  onCompleted?: () => void;
  agencyUser: LoggedInAgencyUserFieldsFragment;
  agencyName: string;
  renewalDate: Date;
  currentPeriod: SubscriptionPeriod;
  currentSeats: number;
  minSeats: number;
  includedSeats: number;
  voiceAgents: number;
}) => {
  const previewRef = React.useRef<SubscriptionCheckoutPreview | null>(null);
  const previewRequestIdRef = React.useRef(0);

  // Standard sunset renewals default to Monthly (10% off) regardless of the
  // agency's current billing period; the user can switch to Annual.
  const [period, setPeriod] = React.useState<SubscriptionPeriod>(SubscriptionPeriod.Monthly);
  const [seats, setSeats] = React.useState(props.currentSeats);
  const [plan, setPlan] = React.useState<SubscriptionPlan>(SubscriptionPlan.Pro);
  const [agreedToTerms, setAgreedToTerms] = React.useState(false);
  const [submitting, setSubmitting] = React.useState(false);
  // Opening the wizard immediately requests a preview, so start in the loading
  // state rather than flashing an empty total.
  const [previewLoading, setPreviewLoading] = React.useState(props.open);
  const [errorMessage, setErrorMessage] = React.useState<string | null | undefined>(null);
  const [checkoutPreview, setCheckoutPreview] = React.useState<SubscriptionCheckoutPreview | null>(
    null
  );
  const [showPlanDetails, setShowPlanDetails] = React.useState(false);

  const checkoutInput = React.useMemo(
    () => ({
      plan,
      period,
      seats,
      voiceAgents: props.voiceAgents
    }),
    [plan, period, seats, props.voiceAgents]
  );

  const loadPreview = React.useCallback(async () => {
    const requestId = ++previewRequestIdRef.current;
    const { preview, error } = await previewSubscriptionCheckout(checkoutInput);
    if (requestId !== previewRequestIdRef.current) {
      return false;
    }
    if (error || !preview) {
      setErrorMessage(error);
      setCheckoutPreview(null);
      previewRef.current = null;
      return false;
    }
    setCheckoutPreview(preview);
    previewRef.current = preview;
    setErrorMessage(null);
    return true;
  }, [checkoutInput]);

  React.useEffect(() => {
    if (!props.open) {
      return;
    }
    // The previous totals stay on screen, dimmed, until the new ones arrive so
    // the dialog does not collapse on every seat or plan change.
    setErrorMessage(null);
    let cancelled = false;
    void (async () => {
      setPreviewLoading(true);
      await loadPreview();
      if (!cancelled) {
        setPreviewLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [props.open, checkoutInput, loadPreview]);

  const handleSuccess = () => {
    props.onCompleted?.();
    props.onClose();
  };

  const handleConfirm = async () => {
    if (!checkoutPreview || !agreedToTerms) {
      return;
    }
    setSubmitting(true);
    setErrorMessage(null);
    const result = await finalizeSubscriptionCheckout(checkoutInput, checkoutPreview);
    setSubmitting(false);
    if (result.status === 'error') {
      setErrorMessage(result.error);
      return;
    }
    if (result.status === 'updated' || result.status === 'external') {
      handleSuccess();
    }
  };

  const handleOpenBillingPortal = async () => {
    setSubmitting(true);
    const result = await openBillingPortal();
    setSubmitting(false);
    if (result.error || !result.url) {
      // eslint-disable-next-line no-console
      console.error('[renewal] openBillingPortal failed', result.error);
      return;
    }
    window.open(result.url, '_blank');
  };

  const previewSubTotal = checkoutPreview?.invoicePreview?.subTotal;
  const previewTotal = checkoutPreview?.invoicePreview?.total;
  const hasStripePricing =
    !previewLoading &&
    !errorMessage &&
    checkoutPreview?.invoicePreview != null &&
    previewSubTotal != null &&
    previewSubTotal > 0 &&
    previewTotal != null &&
    previewTotal > 0;

  const needsEmbeddedPayment =
    checkoutPreview?.requiresPaymentMethod && checkoutPreview.clientSecret;
  const canConfirm =
    agreedToTerms &&
    checkoutPreview &&
    !previewLoading &&
    hasStripePricing &&
    (checkoutPreview.canChargePaymentMethodOnFile ||
      (!checkoutPreview.requiresPaymentMethod && !checkoutPreview.requiresExternalCheckout));

  return (
    <Dialog
      open={props.open}
      onClose={() => {
        // Backdrop click / Escape closes like Cancel, except while a submit is in flight.
        if (!submitting) {
          props.onClose();
        }
      }}
      size="3xl"
    >
      {showPlanDetails ? (
        <>
          <RenewalPlanDetailsBody />
          <RenewalPlanDetailsActions onBack={() => setShowPlanDetails(false)} />
        </>
      ) : (
        <>
          <DialogTitle>Review your renewal options</DialogTitle>
          <DialogDescription>
            Cara Standard is being retired. Choose a plan and billing cycle to go in effect at
            renewal.
          </DialogDescription>

          <DialogBody>
            <Fieldset>
              <FieldGroup>
                <Field>
                  <Label>Plan</Label>
                  <Description>
                    <span className="block">
                      Choose the plan that best fits your needs.{' '}
                      <button
                        type="button"
                        onClick={() => setShowPlanDetails(true)}
                        className="font-medium text-sky-700 underline underline-offset-2 hover:text-sky-900 dark:hover:text-sky-200 dark:text-sky-400"
                      >
                        See plan details
                      </button>
                      . Cara Pro provides the best value, retaining all features in your legacy
                      Standard plan plus advanced Pro presets, AMS/CRM integrations, advanced Voice
                      AI, and higher processing priority. Cara Basic is a more affordable option
                      with a more limited feature set.
                    </span>
                  </Description>
                  <div
                    data-slot="control"
                    className="grid grid-cols-1 gap-4 sm:grid-cols-2"
                    role="radiogroup"
                    aria-label="Plan"
                  >
                    {RENEWAL_PLAN_OPTIONS.map((option) => {
                      const selected = plan === option.id;
                      return (
                        <label
                          key={option.id}
                          className={clsx(
                            'group/tier flex cursor-pointer flex-col rounded-xl p-4 ring-1 ring-neutral-200 dark:bg-neutral-800/50 dark:ring-white/15',
                            selected && 'ring-primary-600 ring-2 dark:ring-primary-400'
                          )}
                        >
                          <input
                            type="radio"
                            name="renewal-plan"
                            value={option.id}
                            checked={selected}
                            onChange={() => setPlan(option.id)}
                            className="sr-only"
                          />
                          <div className="flex items-center justify-between gap-x-4">
                            <h3 className="m-0 p-0 text-base/7 font-semibold text-neutral-900 dark:text-white">
                              {option.name}
                            </h3>
                            {option.id === SubscriptionPlan.Pro && (
                              <p className="m-0 rounded-full bg-primary-600/10 px-2.5 py-1 text-xs/5 font-medium text-primary-600 dark:bg-primary-500 dark:text-white">
                                Recommended
                              </p>
                            )}
                          </div>
                          <p className="mt-1 text-sm/6 text-neutral-600 dark:text-neutral-300">
                            {option.description}
                          </p>
                        </label>
                      );
                    })}
                  </div>
                </Field>
                <div className="grid gap-x-6 gap-y-6 sm:grid-cols-2">
                  <Field>
                    <Label>Billing Cycle</Label>
                    <Description>
                      Your renewal includes an additional, gratuitous discount to help ease your
                      transition.
                    </Description>
                    <div data-slot="control">
                      <fieldset
                        aria-label="Payment frequency"
                        className="inline-flex rounded-full bg-neutral-100 p-1 dark:bg-neutral-800"
                      >
                        {[
                          {
                            value: SubscriptionPeriod.Monthly,
                            label: 'Monthly',
                            discount: '10% off'
                          },
                          {
                            value: SubscriptionPeriod.Annual,
                            label: 'Annual',
                            discount: '20% off'
                          }
                        ].map((option) => (
                          <label key={option.value} className="relative cursor-pointer">
                            <input
                              type="radio"
                              name="renewal-billing-cycle"
                              value={option.value}
                              checked={period === option.value}
                              onChange={() => setPeriod(option.value)}
                              aria-label={`${option.label} (${option.discount})`}
                              className="sr-only peer"
                            />
                            <span className="block rounded-full px-4 py-1.5 text-xs font-medium text-neutral-700 peer-checked:text-white peer-checked:bg-primary-600 peer-checked:shadow-sm dark:text-neutral-300 dark:peer-checked:text-white">
                              {option.label}{' '}
                              <span className="font-semibold">({option.discount})</span>
                            </span>
                          </label>
                        ))}
                      </fieldset>
                    </div>
                  </Field>
                  <Field>
                    <Label>Seats</Label>
                    <Description>
                      {props.includedSeats} seats included. You cannot go below your active user
                      count.
                    </Description>
                    <div data-slot="control" className="flex items-center gap-3">
                      <button
                        type="button"
                        aria-label="Decrease seats"
                        onClick={() => setSeats((seats) => Math.max(props.minSeats, seats - 1))}
                        disabled={seats <= props.minSeats}
                        className={clsx(
                          'flex h-8 w-8 items-center justify-center rounded-md border',
                          seats <= props.minSeats
                            ? 'border-neutral-200 bg-neutral-50 text-neutral-300 cursor-not-allowed dark:border-neutral-700 dark:bg-neutral-800 dark:text-neutral-600'
                            : 'border-neutral-300 bg-white text-neutral-700 hover:bg-neutral-50 dark:border-neutral-600 dark:bg-neutral-800 dark:text-neutral-300 dark:hover:bg-neutral-700'
                        )}
                      >
                        <MinusIcon className="h-4 w-4" />
                      </button>
                      <div className="w-24">
                        <Input
                          type="number"
                          min={props.minSeats}
                          value={seats}
                          onChange={(e) => {
                            const value = parseInt(e.target.value, 10);
                            if (!isNaN(value) && value >= props.minSeats) {
                              setSeats(value);
                            }
                          }}
                          className="w-full text-center"
                        />
                      </div>
                      <button
                        type="button"
                        aria-label="Increase seats"
                        onClick={() => setSeats((seats) => seats + 1)}
                        className="flex h-8 w-8 items-center justify-center rounded-md border border-neutral-300 bg-white text-neutral-700 hover:bg-neutral-50 dark:border-neutral-600 dark:bg-neutral-800 dark:text-neutral-300 dark:hover:bg-neutral-700"
                      >
                        <PlusIcon className="h-4 w-4" />
                      </button>
                    </div>
                  </Field>
                </div>
              </FieldGroup>
            </Fieldset>

            {!errorMessage && checkoutPreview?.invoicePreview && (
              <div className="relative mt-8" aria-busy={previewLoading}>
                <div className={clsx(previewLoading && 'opacity-40')}>
                  <InvoicePreview
                    invoicePreview={checkoutPreview.invoicePreview}
                    agencyUser={props.agencyUser}
                    compact
                  />
                </div>
                {previewLoading && (
                  <div className="absolute inset-0 flex items-center justify-center">
                    <Spinner color="neutral" size="size-6" />
                  </div>
                )}
              </div>
            )}

            {!errorMessage && !checkoutPreview?.invoicePreview && previewLoading && (
              <div className="mt-8">
                <InvoicePreviewSkeleton compact />
              </div>
            )}

            <div className="mt-8 rounded-md bg-neutral-50 dark:bg-neutral-800/50 border border-neutral-200 dark:border-neutral-700 px-4 py-3">
              <p className="m-0 text-sm text-neutral-600 dark:text-neutral-400">
                You will be charged on your renewal date
                {(checkoutPreview?.canChargePaymentMethodOnFile ||
                  (checkoutPreview && !needsEmbeddedPayment)) && (
                  <>
                    {' '}
                    using your saved payment method.{' '}
                    <button
                      type="button"
                      onClick={handleOpenBillingPortal}
                      disabled={submitting}
                      className="font-medium text-sky-700 underline underline-offset-2 hover:text-sky-900 dark:hover:text-sky-200 dark:text-sky-400"
                    >
                      Update payment method
                    </button>
                  </>
                )}
                .
              </p>
            </div>

            {needsEmbeddedPayment && checkoutPreview.clientSecret && (
              <div className="mt-6">
                <Elements
                  stripe={stripePromise}
                  options={{ clientSecret: checkoutPreview.clientSecret }}
                >
                  <RenewalMigrationPaymentForm
                    agencyUser={props.agencyUser}
                    plan={plan}
                    period={period}
                    seats={seats}
                    voiceAgents={props.voiceAgents}
                    onSuccess={handleSuccess}
                  />
                </Elements>
              </div>
            )}

            <div className="mt-4">
              <CheckboxField>
                <Checkbox
                  color="sky"
                  checked={agreedToTerms}
                  onChange={(checked: boolean) => setAgreedToTerms(checked)}
                />
                <Label>
                  I agree to the selected renewal plan and the{' '}
                  <a
                    href={STANDARD_PLAN_TERMS_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(event) => event.stopPropagation()}
                    onMouseDown={(event) => event.stopPropagation()}
                    className="font-medium text-sky-700 underline underline-offset-2 dark:text-sky-400"
                  >
                    Cara standard terms
                  </a>
                  .
                </Label>
              </CheckboxField>
            </div>

            <p className="mt-4 text-sm text-neutral-500 dark:text-neutral-400">
              Questions?{' '}
              <a
                href={RENEWAL_CALL_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="underline"
              >
                Book a call
              </a>{' '}
              or{' '}
              <a href={`mailto:${SUPPORT_EMAIL}`} className="underline">
                email support
              </a>
              .
            </p>

            {errorMessage && (
              <div className="mt-4">
                <ErrorMessage>{errorMessage}</ErrorMessage>
                <Button
                  color="white"
                  className="mt-2"
                  disabled={previewLoading}
                  onClick={() => {
                    setErrorMessage(null);
                    setPreviewLoading(true);
                    void loadPreview().finally(() => setPreviewLoading(false));
                  }}
                >
                  Retry
                </Button>
              </div>
            )}
          </DialogBody>

          <DialogActions>
            <Button color="white" type="button" onClick={props.onClose} disabled={submitting}>
              Cancel
            </Button>
            {!needsEmbeddedPayment && (
              <Button
                color="sky"
                loading={submitting}
                disabled={!canConfirm}
                onClick={handleConfirm}
              >
                Confirm renewal
              </Button>
            )}
          </DialogActions>
        </>
      )}
    </Dialog>
  );
};
